import React from "react";

import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { ArticleRow } from "store/types/news";
import { formatNewsDate } from "utils/format-news-date";

const logoInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return name.slice(0, 2).toUpperCase();
  return (words[0][0] + (words[1][0] || "")).toUpperCase();
};

type Props = {
  articles: ArticleRow[];
  onDealCoverage?: (dealId: string) => void;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ArticleList: React.FC<Props> = ({ articles, onDealCoverage }) => {
  return (
    <Stack direction="column" gap="16px">
      {articles.map((article, idx) => {
        return (
          <Stack
            key={article.id ?? idx}
            direction="column"
            gap="16px"
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: "8px",
              boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
              p: "16px",
              overflow: "hidden",
              transition: "box-shadow 0.12s",
              "&:hover": {
                boxShadow: "0 2px 8px rgba(7,31,53,0.08)"
              }
            }}
          >
            <Stack direction="row" alignItems="center" gap="10px" minWidth={0}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "6px",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                  fontWeight: 600,
                  flexShrink: 0
                }}
              >
                {logoInitials(article.source || article.category || "")}
              </Box>
              <Stack justifyContent="space-between" sx={{ height: "40px", minWidth: 0, alignSelf: "stretch" }}>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    lineHeight: "24px",
                    color: "#071F35",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {article.source}
                </Typography>
                <Stack direction="row" gap="4px" alignItems="center">
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: "16px",
                      color: "#496682",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {article.category} |
                  </Typography>
                  <Typography sx={{ color: "#071F35", fontSize: "14px", lineHeight: "16px" }}>
                    {formatNewsDate(article.published_at)}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack gap="8px">
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  lineHeight: "28px",
                  color: "#071F35",
                  wordBreak: "break-word"
                }}
              >
                {article.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  color: "#071F35"
                }}
              >
                {article.summary}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" alignItems="center">
              <Button
                component="a"
                href={article.url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                disableRipple
                endIcon={<KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
                sx={{
                  flexShrink: 0,
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "16px",
                  color: "#26A6AE",
                  textTransform: "none",
                  borderRadius: "32px",
                  gap: "4px",
                  px: "8px",
                  py: "4px",
                  minWidth: 0,
                  "& .MuiButton-endIcon": { ml: 0 },
                  "&:hover": { bgcolor: "rgba(38, 166, 174, 0.08)" }
                }}
              >
                See Item
              </Button>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};
